/**
 * Special Art Books · shared behaviour (vanilla JS, no libraries). Loaded with `defer` by the layouts.
 * - Dialogs: [data-dialog-open="id"] opens <dialog id> as a modal, [data-dialog-close] closes it, a click on the
 *   backdrop closes it. While a dialog is open, html.sab-has-dialog locks the page scroll; on close, focus goes back
 *   to the opener. The dialog receives a bubbling 'sab:dialog-open' event with { opener } in detail.
 * - Disclosure menus (<details data-sab-menu>): one open at a time, Esc and outside click close them.
 * - Scroll: html.is-scrolled and .sab-header.is-scrolled after the first pixels (header shadow).
 * - Lazy backgrounds: [data-lazy-bg] gets .is-near when it comes within 600 px of the screen.
 * - Newsletter: SAB.mlSubmit(form, action, extra) sends a MailerLite Classic form (CORS fetch, JSON) and resolves true only when
 *   MailerLite answers { success: true } (every newsletter form of the theme uses it, never an optimistic success).
 * Listeners are delegated on document, so sections re-rendered by the theme editor keep working.
 */
(() => {
  if (window.SAB_BASE) return;
  window.SAB_BASE = true;

  const doc = document;
  const root = doc.documentElement;

  /* QA 18/10/2026: the scroll lock follows the real state of the dialogs (any <dialog open> on the page), so it never stays
     on if a browser skips the 'close' event (e.g. Android back gesture or Esc in quick succession). */
  const syncDialogLock = () => root.classList.toggle('sab-has-dialog', Boolean(doc.querySelector('dialog[open]')));
  if ('MutationObserver' in window) {
    new MutationObserver((records) => {
      if (records.some((r) => r.target instanceof HTMLDialogElement)) syncDialogLock();
    }).observe(root, { subtree: true, attributes: true, attributeFilter: ['open'] });
  }

  /** @param {HTMLDialogElement} dialog @param {Element | null} opener */
  const openDialog = (dialog, opener) => {
    if (typeof dialog.showModal !== 'function' || dialog.open) return;
    dialog.showModal();
    root.classList.add('sab-has-dialog');
    dialog.addEventListener(
      'close',
      () => {
        if (!doc.querySelector('dialog[open]')) root.classList.remove('sab-has-dialog');
        if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
      },
      { once: true },
    );
    dialog.dispatchEvent(new CustomEvent('sab:dialog-open', { bubbles: true, detail: { opener } }));
  };

  doc.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const opener = target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = doc.getElementById(opener.getAttribute('data-dialog-open') || '');
      if (dialog instanceof HTMLDialogElement) {
        event.preventDefault();
        openDialog(dialog, opener);
      }
      return;
    }

    const closer = target.closest('[data-dialog-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }

    if (target instanceof HTMLDialogElement && target.open) {
      const rect = target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) target.close();
      return;
    }

    for (const menu of doc.querySelectorAll('details[data-sab-menu][open]')) {
      if (!menu.contains(target)) menu.removeAttribute('open');
    }
  });

  doc.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    for (const menu of doc.querySelectorAll('details[data-sab-menu][open]')) {
      menu.removeAttribute('open');
      menu.querySelector('summary')?.focus();
    }
  });

  doc.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.hasAttribute('data-sab-menu') || !details.open) return;
      for (const other of doc.querySelectorAll('details[data-sab-menu][open]')) {
        if (other !== details && !other.contains(details)) other.removeAttribute('open');
      }
    },
    { capture: true },
  );

  /* Background patterns ([data-lazy-bg]) load only when the element is within 600 px of the screen: this adds .is-near,
     the section CSS hides the background image until then (`.js [data-lazy-bg]:not(.is-near)`). */
  const watchLazyBg = (scope) => {
    const items = [...scope.querySelectorAll('[data-lazy-bg]:not(.is-near)')];
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-near'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-near');
          io.unobserve(entry.target);
        }),
      { rootMargin: '600px 0px' },
    );
    items.forEach((el) => io.observe(el));
  };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', () => watchLazyBg(doc), { once: true });
  else watchLazyBg(doc);
  doc.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) watchLazyBg(event.target);
  });

  /**
   * SAB.mlSubmit(form, action, extra) → Promise<boolean>
   * MailerLite Classic webform: GET <action>?fields[email]=…&ml-submit=1&anticsrf=true&ajax=1 (+ the other fields[…] /
   * groups[…] of the form and the `extra` pairs, e.g. gdpr[]=Email). 18/10/2026: the endpoint answers plain JSON
   * (application/json) with CORS «*», not JSONP: a <script> tag is refused by the browser (MIME application/json), so the
   * call is a CORS fetch, sent only at submit time (after the email and consent checks). Resolves true ONLY with
   * { success: true }; false on success false, network error, empty action or no answer within 10 s.
   * @param {HTMLFormElement | null} form
   * @param {string} action
   * @param {string | Record<string, string> | URLSearchParams} [extra]
   * @returns {Promise<boolean>}
   */
  const SAB = (window.SAB = window.SAB || {});
  SAB.mlSubmit = async (form, action, extra) => {
    const url = String(action || '').trim();
    if (!url || !/^https:\/\//.test(url)) return false;
    const params = new URLSearchParams();
    if (form instanceof HTMLFormElement) {
      for (const [key, value] of new FormData(form)) {
        if (typeof value === 'string' && (key.startsWith('fields[') || key.startsWith('groups['))) params.append(key, value.trim());
      }
    }
    if (extra) {
      const more = extra instanceof URLSearchParams ? extra : new URLSearchParams(extra);
      for (const [key, value] of more) if (key) params.set(key.trim(), value.trim());
    }
    params.set('ml-submit', '1');
    params.set('anticsrf', 'true');
    params.set('ajax', '1');
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = window.setTimeout(() => ctrl && ctrl.abort(), 10000);
    try {
      const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}${params.toString()}`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        signal: ctrl ? ctrl.signal : undefined,
      });
      const data = await res.json();
      return Boolean(data && data.success === true);
    } catch {
      return false;
    } finally {
      window.clearTimeout(timer);
    }
  };

  let ticking = false;
  const onScroll = () => {
    ticking = false;
    const scrolled = window.scrollY > 4;
    root.classList.toggle('is-scrolled', scrolled);
    doc.querySelector('.sab-header')?.classList.toggle('is-scrolled', scrolled);
  };
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(onScroll);
    },
    { passive: true },
  );
  onScroll();
})();
